import React, { useState } from 'react';
import {
  Box, Drawer, List, ListItemButton, ListItemText, Typography, Divider, Checkbox,
  Menu, useTheme, useMediaQuery,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import { tokens, type, CONTENT_MAX_WIDTH } from '../theme';

/**
 * Horizontally scrollable row of filter pills, each opening a bottom sheet of options.
 *
 * Replaces six outlined form fields with notched labels. Those were the affordance
 * for typing into a form; these are selectors for browsing, and their option counts
 * range from 3 (season) to 255 (team) — a boxed field for each was both heavy and
 * uniform where the content isn't. The labels are dropped because the values are
 * self-describing: "25/26" doesn't need "SEASON" above it.
 *
 * filters: [{ key, label, value, options: [{ value, label }], onChange, multiple }]
 */
const FilterBar = ({ filters }) => {
  const theme = useTheme();
  // A bottom sheet is a phone convention; on a desktop pointer the options belong in
  // a dropdown anchored to the control you clicked.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openKey, setOpenKey] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const active = filters.find(f => f.key === openKey) || null;

  const openFilter = (key, el) => { setAnchorEl(el); setOpenKey(key); };
  const closeFilter = () => { setOpenKey(null); setAnchorEl(null); };

  const displayValue = (f) => {
    if (f.nameAtDefault && f.isDefault && f.isDefault(f.value)) return f.label;
    if (f.multiple) {
      const vals = Array.isArray(f.value) ? f.value : [];
      if (vals.length === 0) return f.label;
      if (vals.length === 1) {
        return (f.options.find(o => String(o.value) === String(vals[0])) || {}).label || vals[0];
      }
      return `${vals.length} ${f.label.toLowerCase()}s`;
    }
    const match = f.options.find(o => String(o.value) === String(f.value));
    return match ? match.label : f.label;
  };

  const isChosen = (f, optValue) => {
    if (f.multiple) {
      const vals = Array.isArray(f.value) ? f.value : [];
      return vals.map(String).includes(String(optValue));
    }
    return String(f.value) === String(optValue);
  };

  const choose = (f, optValue) => {
    if (f.multiple) {
      const vals = (Array.isArray(f.value) ? f.value : []).map(String);
      const next = vals.includes(String(optValue))
        ? vals.filter(v => v !== String(optValue))
        : [...vals, String(optValue)];
      if (next.length === 0) return; // never leave a multi-select empty
      f.onChange(next);
      return;
    }
    f.onChange(optValue);
    closeFilter();
  };

  return (
    <>
      {/* The band sits on the page gutter, which App.js's content column now
          provides for every page. It used to carry a matching mx of its own,
          because that column set no padding and the cards below defined their
          own — so the band would otherwise fill all 1080px while the cards sat
          24px inside, visibly overhanging them on both sides. With the gutter
          defined in one place, an mx here would inset the band twice. */}
      <Box sx={{
        backgroundColor: tokens.surface,
        borderBottom: `1px solid ${tokens.rule}`,
      }}>
      <Box
        sx={{
          display: 'flex',
          gap: 0.75,
          maxWidth: CONTENT_MAX_WIDTH,
          mx: 'auto',
          py: 1,
          overflowX: 'auto',
          // Fade the right edge so it reads as scrollable rather than clipped.
          maskImage: 'linear-gradient(to right, #000 calc(100% - 28px), transparent)',
          WebkitMaskImage: 'linear-gradient(to right, #000 calc(100% - 28px), transparent)',
          // Hide the scrollbar but keep the scroll — the row is meant to read as a
          // single strip of controls, not a scrolling panel.
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {filters.filter(f => f.options && f.options.length > 0).map((f) => {
          const isDefault = f.isDefault ? f.isDefault(f.value) : false;
          return (
            <Box
              key={f.key}
              component="button"
              type="button"
              onClick={(e) => openFilter(f.key, e.currentTarget)}
              aria-label={`${f.label}: ${displayValue(f)}`}
              sx={{
                position: 'relative',
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                minHeight: 34,
                pl: 1.5,
                pr: 0.75,
                borderRadius: 999,
                cursor: 'pointer',
                font: 'inherit',
                fontSize: type.bodySm,
                fontWeight: isDefault ? 500 : 700,
                whiteSpace: 'nowrap',
                // A set filter reads as a tint rather than a solid navy block —
                // several solid pills at once overwhelmed the content beneath.
                border: `1px solid ${isDefault ? tokens.rule : tokens.navy}`,
                backgroundColor: isDefault ? tokens.surface : tokens.navyTint,
                color: isDefault ? tokens.inkMuted : tokens.navy,
                // Keep a 44px touch target without a 44px-tall pill.
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  height: 44,
                  transform: 'translateY(-50%)',
                },
                '&:hover': {
                  backgroundColor: tokens.navyTint,
                  borderColor: tokens.navy,
                },
              }}
            >
              {displayValue(f)}
              <ExpandMoreIcon sx={{ fontSize: 17, opacity: 0.6 }} />
            </Box>
          );
        })}
      </Box>
      </Box>

      {active && !isMobile && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(active)}
          onClose={closeFilter}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { maxHeight: 420, minWidth: 220, mt: 0.5 } } }}
        >
          {active.options.map((opt) => {
            const chosen = isChosen(active, opt.value);
            return (
              <ListItemButton
                key={String(opt.value)}
                onClick={() => choose(active, opt.value)}
                sx={{ minHeight: 40, px: 2 }}
              >
                {active.multiple && (
                  <Checkbox edge="start" checked={chosen} tabIndex={-1} disableRipple sx={{ mr: 1, p: 0.5 }} />
                )}
                <ListItemText
                  primary={opt.label}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: type.bodySm,
                      fontWeight: chosen ? 700 : 400,
                      color: chosen ? tokens.navy : tokens.ink,
                    },
                  }}
                />
                {!active.multiple && chosen && <CheckIcon sx={{ fontSize: 18, color: tokens.navy }} />}
              </ListItemButton>
            );
          })}
        </Menu>
      )}

      <Drawer
        anchor="bottom"
        open={Boolean(active) && isMobile}
        onClose={closeFilter}
        PaperProps={{ sx: { maxHeight: '72vh', borderRadius: 0 } }}
      >
        {active && (
          <>
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: type.lead, color: tokens.ink }}>
                {active.label}
              </Typography>
              {active.multiple && (
                <Typography sx={{ fontSize: type.caption, color: tokens.inkMuted, mt: 0.25 }}>
                  Choose one or more
                </Typography>
              )}
            </Box>
            <Divider />
            <List sx={{ py: 0, overflowY: 'auto' }}>
              {active.options.map((opt) => {
                const chosen = isChosen(active, opt.value);
                return (
                  <ListItemButton
                    key={String(opt.value)}
                    onClick={() => choose(active, opt.value)}
                    sx={{ minHeight: 52, px: 2.5 }}
                  >
                    {active.multiple && (
                      <Checkbox
                        edge="start"
                        checked={chosen}
                        tabIndex={-1}
                        disableRipple
                        sx={{ mr: 1, p: 0.5 }}
                      />
                    )}
                    <ListItemText
                      primary={opt.label}
                      primaryTypographyProps={{
                        sx: {
                          fontSize: type.lead,
                          fontWeight: chosen ? 700 : 400,
                          color: chosen ? tokens.navy : tokens.ink,
                        },
                      }}
                    />
                    {!active.multiple && chosen && (
                      <CheckIcon sx={{ fontSize: 20, color: tokens.navy }} />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </>
        )}
      </Drawer>
    </>
  );
};

export default FilterBar;
